import { FilterOutlined, SwapRightOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Row, Select, Space } from 'antd';
import type { Dayjs } from 'dayjs';

type FilterOption = {
  label: string;
  value: string;
};

type IncidenceRequestsFiltersProps = {
  mobileOnly: boolean;
  showMobileFilters: boolean;
  incidenceTypeOptions: FilterOption[];
  statusOptions: FilterOption[];
  incidenceTypeFilter?: string;
  statusFilter?: string;
  fromDateFilter: Dayjs | null;
  toDateFilter: Dayjs | null;
  onIncidenceTypeFilterChange: (value?: string) => void;
  onStatusFilterChange: (value?: string) => void;
  onFromDateFilterChange: (value: Dayjs | null) => void;
  onToDateFilterChange: (value: Dayjs | null) => void;
  onClearFilters: () => void;
  onDownloadReport: () => void;
  disableDownloadReport?: boolean;
};

export const IncidenceRequestsFilters = ({
  mobileOnly,
  showMobileFilters,
  incidenceTypeOptions,
  statusOptions,
  incidenceTypeFilter,
  statusFilter,
  fromDateFilter,
  toDateFilter,
  onIncidenceTypeFilterChange,
  onStatusFilterChange,
  onFromDateFilterChange,
  onToDateFilterChange,
  onClearFilters,
  onDownloadReport,
  disableDownloadReport = false,
}: IncidenceRequestsFiltersProps) => {
  const dateRangeValue: [Dayjs, Dayjs] | null =
    fromDateFilter && toDateFilter ? [fromDateFilter, toDateFilter] : null;

  if (!mobileOnly) {
    return (
      <Row
        className="employee-incidence-filters employee-incidence-filters--desktop mt-5 mb-5"
        align="middle"
        justify="space-between"
        gutter={[12, 12]}
      >
        <Col flex="auto">
          <Space size={8} wrap>
            <FilterOutlined className="filter-icon" />
            <Select
              allowClear
              className="filter-control"
              placeholder="Tipo de incidencia"
              options={incidenceTypeOptions}
              value={incidenceTypeFilter}
              onChange={onIncidenceTypeFilterChange}
              data-cy="incidence-request-type-filter"
            />
            <DatePicker.RangePicker
              className="filter-control filter-range-control"
              placeholder={['Fecha inicial', 'Fecha final']}
              separator={<SwapRightOutlined />}
              value={dateRangeValue}
              onChange={(value) => {
                onFromDateFilterChange(value?.[0] ?? null);
                onToDateFilterChange(value?.[1] ?? null);
              }}
              data-cy="incidence-request-date-filter"
            />
            <Select
              allowClear
              className="filter-control"
              placeholder="Status"
              options={statusOptions}
              value={statusFilter}
              onChange={onStatusFilterChange}
              data-cy="incidence-request-status-filter"
            />
            <Button
              type="link"
              onClick={onClearFilters}
              className="clean-filters-text"
              data-cy="incidence-request-clear-filters-btn"
            >
              Limpiar filtros
            </Button>
          </Space>
        </Col>
        <Col>
          <Button
            className="download-report-btn"
            onClick={onDownloadReport}
            disabled={disableDownloadReport}
            data-cy="incidence-request-download-report-btn"
          >
            Descargar reporte
          </Button>
        </Col>
      </Row>
    );
  }

  if (!showMobileFilters) {
    return null;
  }

  return (
    <div className="employee-incidence-mobile-filters mt-4 mb-5">
      <Select
        allowClear
        className="filter-control"
        placeholder="Tipo de incidencia"
        options={incidenceTypeOptions}
        value={incidenceTypeFilter}
        onChange={onIncidenceTypeFilterChange}
        data-cy="incidence-request-type-filter"
      />
      <DatePicker.RangePicker
        className="filter-control filter-range-control"
        placeholder={['Fecha inicial', 'Fecha final']}
        separator={<SwapRightOutlined />}
        value={dateRangeValue}
        onChange={(value) => {
          onFromDateFilterChange(value?.[0] ?? null);
          onToDateFilterChange(value?.[1] ?? null);
        }}
        data-cy="incidence-request-date-filter-mobile"
      />
      <Select
        allowClear
        className="filter-control"
        placeholder="Status"
        options={statusOptions}
        value={statusFilter}
        onChange={onStatusFilterChange}
        data-cy="incidence-request-status-filter"
      />
      <div className="mobile-filter-actions">
        <Button
          type="link"
          onClick={onClearFilters}
          className="clean-filters-text"
          data-cy="incidence-request-clear-filters-btn"
        >
          Limpiar filtros
        </Button>
      </div>
      <Button
        className="download-report-btn download-mobile-btn"
        onClick={onDownloadReport}
        disabled={disableDownloadReport}
        data-cy="incidence-request-download-report-btn"
      >
        Descargar reporte
      </Button>
    </div>
  );
};
